import { useReveal } from "@/hooks/useReveal";

const GIA_STONES_IMG = "https://customer-assets.emergentagent.com/job_private-collection-12/artifacts/biycgtek_IMG_2777.jpeg";

// background-position tuned to the composite GIA photo (square ~1900x1900)
// Stones approx: Pear top-center, Heart top-right, Cushion bottom-left, Emerald bottom-center.
const STONES = [
  { key: "heart",   name: "Heart",   text: "The maison's emblem — a stone of devotion and singular fire.",         img: GIA_STONES_IMG, pos: "82% 62%" },
  { key: "pear",    name: "Pear",    text: "Falling light — a teardrop cut of unmistakable elegance.",              img: GIA_STONES_IMG, pos: "38% 55%" },
  { key: "emerald", name: "Emerald", text: "Architectural clarity — the cut of collectors and quiet power.",        img: GIA_STONES_IMG, pos: "55% 88%" },
  { key: "cushion", name: "Cushion", text: "Heritage in geometry — softened corners, ancient brilliance.",         img: GIA_STONES_IMG, pos: "18% 68%" },
];

export default function DiamondsSection({ onRequest }) {
  const eyebrow = useReveal();
  const title = useReveal();
  const lead = useReveal();

  return (
    <section
      id="diamonds"
      data-testid="diamonds-section"
      className="relative bg-[#050505] py-28 md:py-40 overflow-hidden grain"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-16">
          <div className="lg:col-span-7">
            <p ref={eyebrow} className="pd-reveal eyebrow" data-testid="diamonds-eyebrow">
              PiérrecDavís Diamonds
            </p>
            <h2
              ref={title}
              className="pd-reveal mt-6 font-serif-display text-4xl md:text-6xl lg:text-7xl text-white tracking-[0.06em] leading-[1.05]"
              style={{ transitionDelay: "160ms" }}
              data-testid="diamonds-title"
            >
              Four Identities. <br />
              <span className="italic font-serif-body text-gold-gradient">One Standard.</span>
            </h2>
          </div>
          <div ref={lead} className="pd-reveal lg:col-span-5" style={{ transitionDelay: "260ms" }}>
            <div className="hairline mb-6" />
            <p className="font-light text-white/65 text-base md:text-lg leading-relaxed tracking-wide">
              Every stone in the maison's vault is GIA-certified and selected for character —
              not carat alone. Four cuts define our identity, and each is held to the same uncompromising threshold.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {STONES.map((s, i) => (
            <Stone key={s.key} stone={s} index={i} onRequest={onRequest} />
          ))}
        </div>

        <div className="mt-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8 border-t border-white/10 pt-10">
          <p className="text-white/70 font-serif-body italic text-lg md:text-xl max-w-xl">
            "Rise Above The Ordinary."
          </p>
          <button
            data-testid="diamonds-request-btn"
            onClick={onRequest}
            className="btn-gold"
          >
            <span>Inquire About a Stone</span>
          </button>
        </div>
      </div>
    </section>
  );
}

function Stone({ stone, index, onRequest }) {
  const r = useReveal();
  return (
    <div
      ref={r}
      data-testid={`diamond-${stone.key}`}
      style={{ transitionDelay: `${index * 120}ms` }}
      className="pd-reveal group relative overflow-hidden border border-[var(--pd-border)] bg-black"
    >
      <div className="aspect-[3/4] overflow-hidden relative">
        <div
          aria-label={`${stone.name} cut diamond`}
          role="img"
          className="absolute inset-0 transition-transform duration-[2200ms] group-hover:scale-110"
          style={{
            backgroundImage: `url(${stone.img})`,
            backgroundSize: "250%",
            backgroundPosition: stone.pos || "center",
            backgroundRepeat: "no-repeat",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
      </div>
      <div className="absolute inset-0 p-6 flex flex-col justify-end">
        <p className="eyebrow text-white/70">Identity Stone</p>
        <h3 className="font-serif-display text-2xl md:text-3xl text-white tracking-[0.16em] mt-1">{stone.name}</h3>
        <p className="mt-3 text-white/70 text-xs md:text-sm leading-relaxed">{stone.text}</p>
        <button
          onClick={onRequest}
          data-testid={`diamond-${stone.key}-cta`}
          className="mt-5 self-start text-[0.65rem] uppercase tracking-[0.36em] text-[var(--pd-gold)] border-b border-[var(--pd-gold)] pb-0.5 hover:tracking-[0.46em] transition-all duration-500"
        >
          Request Stone →
        </button>
      </div>
    </div>
  );
}
